import { useMemo } from 'react';

import type { OrderStep } from '@viome/viome-base/src/usecase/OrderTracking/OrderTracking.model';
import { useGetOrderTrackingStatus } from '@viome/viome-base/src/usecase/OrderTracking/OrderTrackingQuery';

export interface EnrichedOrderStep extends OrderStep {
  is_active: boolean;
  is_completed: boolean;
}

export interface StepGroup {
  sectionTitle: string;
  steps: EnrichedOrderStep[];
}

const STAGE_LABELS = ['Placed', 'Processing', 'Shipped', 'Delivered'] as const;

const PROGRESS_MAP: Record<number, number> = {
  1: 6,
  2: 30,
  3: 35,
  4: 40,
  5: 50,
  6: 65,
  7: 100,
};

function enrichSteps(
  steps: OrderStep[],
  currentStepOrdinal: number | null,
): EnrichedOrderStep[] {
  return steps.map((step) => ({
    ...step,
    is_active:
      currentStepOrdinal != null && step.ordinal === currentStepOrdinal,
    is_completed:
      currentStepOrdinal != null && step.ordinal < currentStepOrdinal,
  }));
}

function groupStepsByStatus(steps: EnrichedOrderStep[]): StepGroup[] {
  const groups: StepGroup[] = [];
  let currentGroup: StepGroup | null = null;

  for (const step of steps) {
    if (!currentGroup || currentGroup.sectionTitle !== step.smp_status) {
      currentGroup = { sectionTitle: step.smp_status, steps: [] };
      groups.push(currentGroup);
    }
    currentGroup.steps.push(step);
  }

  return groups;
}

export const useOrderTrackingViewModel = () => {
  const {
    data: trackingData,
    isLoading,
    isError,
  } = useGetOrderTrackingStatus();

  const hasOrder = trackingData?.order_info != null;
  const currentStep = trackingData?.current_step_ordinal ?? null;

  const enrichedSteps = useMemo(() => {
    if (!trackingData?.steps.length) return [];
    return enrichSteps(trackingData.steps, currentStep);
  }, [trackingData?.steps, currentStep]);

  const progressPercentage = useMemo(() => {
    if (!currentStep) return 0;
    return PROGRESS_MAP[currentStep] ?? 0;
  }, [currentStep]);

  const activeStep = useMemo(() => {
    return enrichedSteps.find((s) => s.is_active) ?? null;
  }, [enrichedSteps]);

  const stepGroups = useMemo(() => {
    if (!enrichedSteps.length) return [];
    return groupStepsByStatus(enrichedSteps);
  }, [enrichedSteps]);

  const hasCarrierTracking = !!trackingData?.order_info?.carrier;

  return {
    isLoading,
    isError,
    hasOrder,
    progressPercentage,
    stageLabels: STAGE_LABELS,
    activeStep,
    stepGroups,
    hasCarrierTracking,
    orderInfo: trackingData?.order_info ?? null,
  };
};
